import { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, Plus, Edit, Trash2, Save, X, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = {
  president: 'President',
  vice_president: 'Vice President',
  general_secretary: 'General Secretary',
  board_member: 'Board Member'
};

const roleColors = {
  president: 'bg-amber-100 text-amber-800',
  vice_president: 'bg-blue-100 text-blue-800',
  general_secretary: 'bg-green-100 text-green-800',
  board_member: 'bg-slate-100 text-slate-800'
};

export default function AdminCMSLeadership() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    role: 'board_member',
    photo_url: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cms/leadership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching leadership:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`${API}/cms/leadership/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Member updated successfully' });
      } else {
        await axios.post(`${API}/cms/leadership`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Member added successfully' });
      }
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast({ title: 'Error', description: 'Failed to save member', variant: 'destructive' });
    }
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name,
      title: member.title,
      role: member.role,
      photo_url: member.photo_url || '',
      order: member.order || 0
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cms/leadership/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Member deleted successfully' });
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast({ title: 'Error', description: 'Failed to delete member', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', title: '', role: 'board_member', photo_url: '', order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  // Group members by role
  const groupedMembers = members.reduce((acc, member) => {
    const role = member.role || 'board_member';
    if (!acc[role]) acc[role] = [];
    acc[role].push(member);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E285B]"></div>
      </div>
    );
  }

  return (
    <div data-testid="cms-leadership-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B]">Leadership Management</h1>
          <p className="text-slate-500 text-sm mt-1">Manage the leadership team displayed on the About page</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#0E285B] hover:bg-[#0E285B]/90"
          data-testid="add-leadership-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-[#0E285B] mb-4">
            {editingId ? 'Edit Member' : 'Add New Member'}
          </h2>
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                required
                data-testid="leadership-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Prof. Dr., Vice President"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                required
                data-testid="leadership-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="leadership-role-select"
              >
                <option value="president">President</option>
                <option value="vice_president">Vice President</option>
                <option value="general_secretary">General Secretary</option>
                <option value="board_member">Board Member</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="leadership-order-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Photo URL</label>
              <input
                type="text"
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                placeholder="/founders/name.jpg"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                data-testid="leadership-photo-input"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" className="bg-[#0E285B] hover:bg-[#0E285B]/90" data-testid="leadership-save-btn">
                <Save className="h-4 w-4 mr-2" />
                {editingId ? 'Update' : 'Add'} Member
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} data-testid="leadership-cancel-btn">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Members List */}
      {['president', 'vice_president', 'general_secretary', 'board_member'].map((role) => (
        groupedMembers[role]?.length > 0 && (
          <div key={role} className="mb-6">
            <h3 className="font-semibold text-[#0E285B] mb-3 flex items-center gap-2">
              <Crown className="h-4 w-4 text-[#F5A623]" />
              {roleLabels[role]}s ({groupedMembers[role].length})
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedMembers[role].map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-sm shadow-sm border border-slate-200 p-4 flex items-center gap-4"
                  data-testid={`leadership-card-${member.id}`}
                >
                  <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="h-full w-full object-cover" />
                    ) : (
                      <User className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-[#0E285B] truncate">{member.name}</h4>
                    <p className="text-sm text-slate-500 truncate">{member.title}</p>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${roleColors[member.role]}`}>
                      {roleLabels[member.role]}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(member)}
                      data-testid={`edit-leadership-${member.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(member.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-leadership-${member.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {members.length === 0 && (
        <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-12 text-center">
          <Crown className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Leadership Members</h3>
          <p className="text-slate-400 mb-4">Add your first leadership team member</p>
          <Button onClick={() => setShowForm(true)} className="bg-[#0E285B] hover:bg-[#0E285B]/90">
            <Plus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
        </div>
      )}
    </div>
  );
}
